import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiErrorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useProfile, PROFILE_KEY } from "../lib/me";
import { initials } from "../lib/format";
import { isDirty, toBody, toDraft, type ProfileDraft } from "../lib/profileDraft";
import { profileBranch } from "../lib/profileBranch";
import { ProfileFields } from "../components/ProfileFields";
import { ProfileWizard } from "./ProfileWizard";
import type { Profile as ProfileEntity } from "../lib/types";
import {
  Badge,
  Button,
  Card,
  CheckCircleIcon,
  EmptyState,
  LoadingBlock,
  MailIcon,
  PageHeader,
  PhoneIcon,
  SectionCard,
  SignInCard,
  TrophyIcon,
  Workbench,
} from "../components/ui";

/**
 * The contact block a coach hands to every organizer, shown as organizers get
 * it and tracking the form as it is typed in.
 *
 * This is the whole reason the page is worth a screen of its own: a profile is
 * not settings, it is the card stapled to each registration (`RegisterTeam`
 * seeds its contact fields from exactly these three values). Showing the result
 * beside the inputs is what makes "is this how I want to be reached" a question
 * the coach can answer without submitting anything.
 *
 * Dark on a light page, the one place this page raises its voice — the same
 * navy the header and hero use, so it reads as the app speaking rather than as
 * a fourth surface style.
 */
function CoachCard({
  draft,
  email,
  dirty,
}: {
  draft: ProfileDraft;
  email?: string;
  dirty: boolean;
}) {
  const name = draft.displayName.trim();
  const phone = draft.phone.trim();
  return (
    <Card className="animate-rise overflow-hidden border-navy-900 bg-navy-950 p-5 text-white sm:p-6">
      <div className="mb-4 flex items-start justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
          On every registration
        </p>
        {/* Only while the two disagree: a badge that is always there stops
            meaning anything, and this one has one job — telling a coach that
            what they are reading is not yet what organizers would get. */}
        {dirty ? <Badge tone="brand">Unsaved</Badge> : null}
      </div>

      <div className="flex items-center gap-3">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-600 font-display text-base font-bold text-brandFg">
          {/* An empty name would render an empty chip; the em dash keeps the
              card's shape while the field is blank. */}
          {name ? initials(name) : "—"}
        </span>
        <span className="min-w-0">
          <span className="block truncate font-display text-lg font-bold tracking-tight">
            {name || "Your name"}
          </span>
          {/* What this person is TO AN ORGANIZER, which is the only thing the
              card is about. Not "Coach": an admin editing their own profile is
              looking at the same block, and the tier is not what it means
              here. */}
          <span className="block text-xs text-slate-400">Registration contact</span>
        </span>
      </div>

      <dl className="mt-5 space-y-2.5 border-t border-white/10 pt-4 text-sm">
        <div className="flex items-center gap-2.5">
          <dt className="sr-only">Email</dt>
          <MailIcon className="h-4 w-4 shrink-0 text-slate-400" />
          <dd className="min-w-0 truncate text-slate-200">{email ?? "—"}</dd>
        </div>
        <div className="flex items-center gap-2.5">
          <dt className="sr-only">Phone</dt>
          <PhoneIcon className="h-4 w-4 shrink-0 text-slate-400" />
          <dd className={phone ? "min-w-0 truncate text-slate-200" : "text-slate-400"}>
            {phone || "No phone on file"}
          </dd>
        </div>
      </dl>
    </Card>
  );
}

/** The form, mounted only once a profile is in hand so it can seed from it. */
function ProfileForm({ profile }: { profile: ProfileEntity }) {
  const qc = useQueryClient();
  // Seeded once from the profile that mounted this form. `saved` is the copy
  // the dirty check compares against, advanced on each success rather than
  // re-read from the query — the invalidation below refetches, and a draft
  // being compared against an answer still in flight would flicker "Unsaved".
  const [draft, setDraft] = useState<ProfileDraft>(() => toDraft(profile));
  const [saved, setSaved] = useState<ProfileDraft>(() => toDraft(profile));
  const [error, setError] = useState<string>();

  const save = useMutation({
    mutationFn: () => api.put<ProfileEntity>("/me/profile", toBody(draft)),
    onSuccess: (updated) => {
      setSaved(toDraft(updated));
      setError(undefined);
      // By the constant: this is the entry the dashboard greeting and the
      // registration form's contact prefill both read, so both correct
      // themselves the moment a name changes.
      void qc.invalidateQueries({ queryKey: PROFILE_KEY });
    },
    onError: (e) => setError(apiErrorMessage(e, "Could not save your profile.")),
  });

  const dirty = isDirty(draft, saved);
  const complete = draft.displayName.trim().length > 0;

  return (
    <Workbench aside={<CoachCard draft={draft} email={profile.email} dirty={dirty} />}>
      <SectionCard
        title="Your details"
        description="Change these any time. Registrations you have already submitted keep the contact details they were sent with."
        className="animate-rise"
      >
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (complete) save.mutate();
          }}
        >
          <ProfileFields draft={draft} setDraft={setDraft} />

          {/* Read-only rather than absent: a coach checking their contact
              details wants to see the address organizers have, and leaving it
              off the page sends them hunting through the nav for it. Cognito
              owns the value — the API reasserts it from the verified token on
              every save — so there is nothing here to edit. */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
            <p className="text-sm font-medium text-slate-600">Email</p>
            <p className="truncate text-sm text-slate-800">{profile.email ?? "—"}</p>
            <p className="mt-0.5 text-xs text-slate-400">
              This is your sign-in, and it can&rsquo;t be changed here.
            </p>
          </div>

          {error ? <p className="text-sm text-red-600">{error}</p> : null}

          <div className="flex items-center gap-3">
            <Button type="submit" disabled={save.isPending || !complete || !dirty}>
              {save.isPending ? "Saving…" : "Save profile"}
            </Button>
            {/* The live region is always mounted and usually empty. A region
                that appears along with its text announces nothing in most
                screen readers — they watch for a change inside a region that
                was already there. It holds the confirmation only, not the
                button, whose own label changing ("Saving…") is not the result.

                Same word as the button, past tense, and only once there is
                nothing left to save: a confirmation that outlived the next
                keystroke would be claiming something untrue. */}
            <span aria-live="polite" className="text-sm text-brandText">
              {save.isSuccess && !dirty ? (
                <span className="flex animate-fade-in items-center gap-1.5">
                  <CheckCircleIcon className="h-4 w-4" />
                  Saved
                </span>
              ) : null}
            </span>
          </div>
        </form>
      </SectionCard>
    </Workbench>
  );
}

/**
 * The coach's own profile (spec §8.1): edit the details the signup wizard
 * collected. Same four-branch ladder every /me page uses — a coach who never
 * finished the wizard gets the wizard here rather than a form seeded from
 * nothing.
 */
export function Profile() {
  const { ready, idToken } = useAuth();
  const profileQ = useProfile();

  // Decided by the pure discriminant so the error branch is assertable — it is
  // the one no page test can reach, and the one that used to be destructive.
  const branch = profileBranch({
    ready,
    signedIn: !!idToken,
    isError: profileQ.isError,
    profile: profileQ.data,
  });

  if (branch === "loading") return <LoadingBlock label="Loading your profile…" />;
  if (branch === "signedOut") {
    return (
      <SignInCard
        title="Your profile"
        hint="Sign in to update your name and contact details."
        next="/me/profile"
      />
    );
  }
  // Before the wizard, for the reason the dashboard's two sections give: a
  // failed request has no profile either, and falling through would hand a
  // set-up coach an empty wizard whose first save wipes their phone.
  if (branch === "error") {
    return (
      <EmptyState
        icon={<TrophyIcon className="h-5 w-5" />}
        title="Couldn't load your profile"
        hint="Check your connection and try again."
      />
    );
  }
  if (branch === "setup") return <ProfileWizard />;
  // Unreachable: "form" is the only branch left, and it means data is present.
  if (!profileQ.data) return <LoadingBlock label="Loading your profile…" />;

  return (
    // Clamped well inside the 1536px shell, the way Layout's frame asks forms
    // to: two short fields stretched across a wide monitor put the label a
    // hand's width from the value, and the rail would drift off on its own.
    <div className="mx-auto w-full max-w-5xl space-y-8">
      <PageHeader
        eyebrow="Account"
        title="Your profile"
        subtitle="Your name and number travel with every team you register."
      />
      {/* Keyed by uid so switching accounts re-seeds the draft instead of
          leaving the previous coach's name in the form. */}
      <ProfileForm key={profileQ.data.uid} profile={profileQ.data} />
    </div>
  );
}
